import { useCallback, useMemo } from 'react';
import { TFunction } from 'i18next';

import {
  useErpCourseLessonSkillsQuery,
  useErpGroupLessonSkillsQuery,
  usePushCourseLessonSkillsToGroupsMutation,
  useSaveCourseLessonSkillsMutation,
  useSaveGroupLessonSkillsMutation,
} from 'core/graphql';

/** ТЗ 0126 — одна строка «навык на занятии». */
export interface ILessonSkillRow {
  id: string;
  /** Номер занятия: программы курса или места в расписании группы. */
  lessonNumber: number | null;
  /** У группы — строка её расписания; у курса — `null`. */
  groupLessonId: string | null;
  skillId: string;
  skillName: string;
  skillSlug: string;
  skillCategory: string;
  skillIconUrl: string | null;
  level: number;
  howTrained: string;
  order: number;
}

/** Отказ сервера по навыкам занятий (`customErrors`). */
export interface ILessonSkillsError {
  code: string;
  field: string;
  message: string;
}

/** Строка редактора: то, что уходит на сервер. */
export interface ILessonSkillDraft {
  skillId: string;
  level: number;
  howTrained: string;
}

export interface ILessonSkillsSaveResult {
  success: boolean;
  added: number;
  updated: number;
  removed: number;
  errors: ILessonSkillsError[];
}

export interface IApplyLessonSkillsResult {
  success: boolean;
  groups: number;
  created: number;
  updated: number;
  beyondProgram: number;
  errors: ILessonSkillsError[];
}

const EMPTY: ILessonSkillRow[] = [];

type TRawRow = {
  id: string;
  lessonNumber?: number | null;
  groupLessonId?: string | null;
  level: number;
  howTrained: string;
  order: number;
  skill: {
    id: string;
    name: string;
    slug: string;
    category: string;
    iconUrl?: string | null;
  };
};

const toRow = (row: TRawRow): ILessonSkillRow => ({
  id: row.id,
  lessonNumber: row.lessonNumber ?? null,
  groupLessonId: row.groupLessonId ?? null,
  skillId: row.skill.id,
  skillName: row.skill.name,
  skillSlug: row.skill.slug,
  skillCategory: row.skill.category,
  skillIconUrl: row.skill.iconUrl ?? null,
  level: row.level,
  howTrained: row.howTrained,
  order: row.order,
});

const toInputRows = (rows: ILessonSkillDraft[]) =>
  rows.map((row, index) => ({
    skillId: row.skillId,
    level: row.level,
    // Пустая строка явно, а не пропуск: занятие пишется целиком, и
    // стёртое описание обязано стереться и на сервере.
    howTrained: row.howTrained.trim(),
    order: index,
  }));

const toErrors = (
  items?:
    | ({
        code?: string | null;
        field?: string | null;
        message?: string | null;
      } | null)[]
    | null,
): ILessonSkillsError[] =>
  (items ?? []).map((item) => ({
    code: item?.code ?? '',
    field: item?.field ?? '',
    message: item?.message ?? '',
  }));

/** Коды `customErrors` навыков занятий, у которых есть своя фраза. */
const LESSON_SKILLS_ERROR_CODES = ['invalid', 'not_found'];

/**
 * Отказ сервера — фразой интерфейса по коду (ru/pl), а сырой текст сервера
 * (он по-русски и называет конкретику: «навык 12 указан дважды») — только
 * подробностью после неё. Неизвестный код — общая фраза + подробность.
 */
export const lessonSkillsErrorText = (
  t: TFunction,
  errors: ILessonSkillsError[],
): string => {
  const first = errors[0];
  const message =
    first && LESSON_SKILLS_ERROR_CODES.includes(first.code)
      ? t(`pages.lessonSkills.errors.${first.code}`)
      : t('pages.careerCenter.errors.generic');
  const detail = first?.message?.trim();
  return detail
    ? t('pages.lessonSkills.errors.withDetail', { message, detail })
    : message;
};

/** ТЗ 0126 — навыки по занятиям курса (шаблон для групп). */
export const useCourseLessonSkills = (courseId: string | null) => {
  const { data, loading, error, refetch } = useErpCourseLessonSkillsQuery({
    variables: { courseId: courseId as string },
    skip: !courseId,
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo(
    () => data?.careerCourseLessonSkills?.map(toRow) ?? EMPTY,
    [data],
  );

  return { rows: rows.length ? rows : EMPTY, loading, error, refetch };
};

/** ТЗ 0126 — навыки по занятиям группы (её собственное расписание). */
export const useGroupLessonSkills = (groupId: string | null) => {
  const { data, loading, error, refetch } = useErpGroupLessonSkillsQuery({
    variables: { groupId: groupId as string },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo(
    () => data?.careerGroupLessonSkills?.map(toRow) ?? EMPTY,
    [data],
  );

  return { rows: rows.length ? rows : EMPTY, loading, error, refetch };
};

/**
 * Сохранить навыки ОДНОГО занятия курса.
 *
 * Сервер после записи пересобирает навыки курса (`CourseSkill`, «откуда»
 * навык открывается), поэтому таблица навыков курса перечитывается вместе с
 * этой. `ErpCareerCourseSkills` безопасно звать по имени: `courseId` у него
 * необязателен, пустых переменных (400) не бывает.
 */
export const useSetCourseLessonSkills = () => {
  const [save, { loading }] = useSaveCourseLessonSkillsMutation({
    refetchQueries: ['ErpCareerCourseSkills'],
    awaitRefetchQueries: true,
  });

  const setLessonSkills = useCallback(
    async (
      courseId: string,
      lessonNumber: number,
      rows: ILessonSkillDraft[],
    ): Promise<ILessonSkillsSaveResult> => {
      const { data } = await save({
        variables: {
          courseId,
          lessons: [{ lessonNumber, rows: toInputRows(rows) }],
        },
      });
      const payload = data?.setCourseLessonSkills;
      return {
        success: Boolean(payload?.success),
        added: payload?.added ?? 0,
        updated: payload?.updated ?? 0,
        removed: payload?.removed ?? 0,
        errors: toErrors(payload?.customErrors),
      };
    },
    [save],
  );

  return { setLessonSkills, saving: loading };
};

/**
 * Сохранить навыки ОДНОГО занятия группы. Навыки группы (`GroupSkill`)
 * сервер пересобирает сам; их запрос перечитывает вызывающий экран — у
 * `ErpGroupSkills` обязательный `groupId`, и refetch по имени задел бы
 * skip-нутую копию запроса (400 → /500).
 */
export const useSetGroupLessonSkills = () => {
  const [save, { loading }] = useSaveGroupLessonSkillsMutation();

  const setLessonSkills = useCallback(
    async (
      groupId: string,
      groupLessonId: string,
      rows: ILessonSkillDraft[],
    ): Promise<ILessonSkillsSaveResult> => {
      const { data } = await save({
        variables: {
          groupId,
          lessons: [{ groupLessonId, rows: toInputRows(rows) }],
        },
      });
      const payload = data?.setGroupLessonSkills;
      return {
        success: Boolean(payload?.success),
        added: payload?.added ?? 0,
        updated: payload?.updated ?? 0,
        removed: payload?.removed ?? 0,
        errors: toErrors(payload?.customErrors),
      };
    },
    [save],
  );

  return { setLessonSkills, saving: loading };
};

/** ТЗ 0126 — перенести навыки занятий курса во все его группы. */
export const useApplyCourseLessonSkills = () => {
  const [apply, { loading }] = usePushCourseLessonSkillsToGroupsMutation();

  const applyToGroups = useCallback(
    async (courseId: string): Promise<IApplyLessonSkillsResult> => {
      const { data } = await apply({ variables: { courseId } });
      const payload = data?.applyCourseLessonSkillsToGroups;
      return {
        success: Boolean(payload?.success),
        groups: payload?.groups ?? 0,
        created: payload?.created ?? 0,
        updated: payload?.updated ?? 0,
        beyondProgram: payload?.beyondProgram ?? 0,
        errors: toErrors(payload?.customErrors),
      };
    },
    [apply],
  );

  return { applyToGroups, applying: loading };
};
